<script setup lang="ts">
import { computed, markRaw, ref } from 'vue'

import IconSettingContainer from '@/components/atoms/icons/IconSettingContainer.vue'
import IconSettingLayout from '@/components/atoms/icons/IconSettingLayout.vue'
import SettingImageSelector from '@/components/beta-editor/settings/controls/BetaSettingImageSelector.vue'
import SettingMargin from '@/components/atoms/SettingMargin.vue'
import SettingOpacity from '@/components/atoms/SettingOpacity.vue'
import SettingPadding from '@/components/atoms/SettingPadding.vue'
import SettingWidth from '@/components/atoms/SettingWidth.vue'
import EditorSidebarLayout from '@/components/layouts/EditorSidebarLayout.vue'
import SettingsLayout from '@/components/layouts/SettingsLayout.vue'
import BetaSettingColorPicker from '@/components/beta-editor/settings/controls/BetaSettingColorPicker.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useLayoutStore } from '@/stores/layout'

const betaStore = useEditorBetaStore()
const layoutStore = useLayoutStore()

const elementForEdit = computed(() => betaStore.elementForEdit)
const backgroundColor = computed(() => (
    elementForEdit.value?.extra?.background_color
    || layoutStore.getSectionBackgroundColorDefault(elementForEdit.value?.sectionIndex ?? 0)
    || ''
))
const backgroundImage = computed(() => elementForEdit.value?.extra?.background_image ?? null)
const backgroundImageOpacity = computed(() => elementForEdit.value?.extra?.background_image_opacity ?? '80')
const backgroundFocalPoint = computed(() => elementForEdit.value?.extra?.background_image_focal_point ?? { x: 50, y: 50 })
const settings = ref([
    { id: 1, name: 'row', icon: markRaw(IconSettingContainer) },
    { id: 2, name: 'layout', icon: markRaw(IconSettingLayout) },
])

const onChangeBackgroundColor = (color: string) => {
    if (!elementForEdit.value) return

    elementForEdit.value.extra = {
        ...elementForEdit.value.extra,
        background_color: color,
    }

    betaStore.updateElementForEdit()
}

const onChangeImageSelector = (event: any) => {
    if (!elementForEdit.value) return

    if (event.target === 'image') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            background_image: event.value
                ? {
                    id: event.value.id,
                    uri: event.value.content,
                    width: event.value.width,
                    height: event.value.height,
                }
                : {
                    id: null,
                    uri: null,
                    width: null,
                    height: null,
                },
        }
    }

    if (event.target === 'focal-point') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            background_image_focal_point: event.value,
        }
    }

    betaStore.updateElementForEdit()
}

const onChangeBackgroundImageOpacity = (opacity: number) => {
    if (!elementForEdit.value) return

    elementForEdit.value.extra = {
        ...elementForEdit.value.extra,
        background_image_opacity: opacity,
    }

    betaStore.updateElementForEdit()
}

const onChangeWidth = (event: any) => {
    if (!elementForEdit.value) return

    if (event.target === 'desktop') elementForEdit.value.grid = event.value
    if (event.target === 'mobile') elementForEdit.value.gridMobile = event.value

    betaStore.updateElementForEdit()
}

const onChangeSpacing = (event: any, target: 'margin' | 'padding') => {
    if (!elementForEdit.value) return

    if (event.config === 'desktop') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [target]: {
                ...elementForEdit.value.extra?.[target],
                [event.target]: event.value,
            },
        }
    }

    if (event.config === 'mobile') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [`${target}_mobile`]: {
                ...elementForEdit.value.extra?.[`${target}_mobile`],
                [event.target]: event.value,
            },
        }
    }

    if (event.config === 'lock') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [`${target}_mobile_lock`]: event.value,
        }
    }

    betaStore.updateElementForEdit()
}
</script>

<template>
    <editor-sidebar-layout v-if="elementForEdit" :settings="settings">
        <template #row>
            <settings-layout name="Configurações do Fundo" :default-open="true">
                <beta-setting-color-picker
                    label="Cor de Fundo"
                    :value="backgroundColor"
                    :default-value="layoutStore.getSectionBackgroundColorDefault(elementForEdit.sectionIndex) ?? ''"
                    clear-on-default
                    @change="onChangeBackgroundColor"
                />

                <setting-image-selector
                    label="Imagem de Fundo"
                    :value="backgroundImage?.uri"
                    with-focal-point
                    with-remove
                    :focal-point="backgroundFocalPoint"
                    @change="onChangeImageSelector"
                />

                <setting-opacity
                    v-if="backgroundImage?.uri"
                    label="Opacidade da imagem"
                    :value="backgroundImageOpacity"
                    @change="onChangeBackgroundImageOpacity"
                />
            </settings-layout>
        </template>

        <template #layout>
            <settings-layout name="Layout da Linha" :default-open="true">
                <setting-width
                    label="Largura"
                    :value="elementForEdit.grid"
                    :value-mobile="elementForEdit.gridMobile"
                    :values-permission-desktop="[12, 99]"
                    :values-permission-mobile="[12, 99]"
                    @change="onChangeWidth"
                />
            </settings-layout>

            <settings-layout name="Avançado">
                <setting-margin
                    label="Margem Externa"
                    :value="elementForEdit.extra?.margin"
                    :value-mobile="elementForEdit.extra?.margin_mobile"
                    :lock="elementForEdit.extra?.margin_mobile_lock"
                    @change="(event: any) => onChangeSpacing(event, 'margin')"
                />

                <setting-padding
                    label="Margem Interna"
                    :value="elementForEdit.extra?.padding"
                    :value-mobile="elementForEdit.extra?.padding_mobile"
                    :lock="elementForEdit.extra?.padding_mobile_lock"
                    @change="(event: any) => onChangeSpacing(event, 'padding')"
                />
            </settings-layout>
        </template>
    </editor-sidebar-layout>
</template>
